import React, { useEffect, useState } from 'react';
import { Card, CardBody, Input, Button, Spinner, Row, Col, Table } from 'reactstrap';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import {
  getAllBusinesses,
  updateBusinessAppFee,
  updateLocationAppFee
} from '../../../axios/appFee';

const BusinessLocationAppFees = () => {
  const [businesses, setBusinesses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 2;

  const filteredBusinesses = businesses
    .map((business) => {
      const term = searchTerm?.toLowerCase();

      const businessMatch =
        business.name.toLowerCase().includes(term) || business.id?.toString().includes(term);

      const matchedLocations = business.locations.filter(
        (loc) => loc.name.toLowerCase().includes(term) || loc.id?.toString().includes(term)
      );

      // If business name or ID matches, return full business (all locations)
      if (businessMatch) {
        return { ...business };
      }

      // If location(s) match, return business with only matching locations
      if (matchedLocations.length > 0) {
        return {
          ...business,
          locations: matchedLocations
        };
      }

      return null;
    })
    .filter(Boolean); // Remove nulls

  const totalPages = Math.ceil(filteredBusinesses.length / rowsPerPage);
  const paginatedBusinesses = filteredBusinesses.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const handleBusinessFeeChange = (id, value) => {
    setBusinesses((prev) => prev.map((b) => (b.id === id ? { ...b, defaultAppFee: value } : b)));
  };

  const handleBusinessFeeUnitChange = (id, unit) => {
    setBusinesses((prev) => prev.map((b) => (b.id === id ? { ...b, defaultAppFeeUnit: unit } : b)));
  };

  const handleLocationFeeChange = (businessId, locationId, value) => {
    setBusinesses((prev) =>
      prev.map((b) =>
        b.id === businessId
          ? {
              ...b,
              locations: b.locations.map((l) => (l.id === locationId ? { ...l, appFee: value } : l))
            }
          : b
      )
    );
  };

  const handleLocationFeeUnitChange = (businessId, locationId, unit) => {
    setBusinesses((prev) =>
      prev.map((b) =>
        b.id === businessId
          ? {
              ...b,
              locations: b.locations.map((l) =>
                l.id === locationId ? { ...l, appFeeUnit: unit } : l
              )
            }
          : b
      )
    );
  };

  const saveBusinessFee = async (business) => {
    const key = `business-${business.id}`;
    setSaving((prev) => ({ ...prev, [key]: true }));

    try {
      let payload = {
        businessId: business.id,
        applicationFeeAmount: business.defaultAppFee,
        applicationFeeUnit: business.defaultAppFeeUnit || 'percentage'
      };
      let data = await updateBusinessAppFee(payload);
      if (data.status === 200 || data.status === 201) {
        toast.success('Business fee updated');
      }
    } catch (err) {
      toast.error('Failed to update business fee');
    } finally {
      setSaving((prev) => ({ ...prev, [key]: false }));
    }
  };

  const saveLocationFee = async (location) => {
    const key = `location-${location.id}`;
    setSaving((prev) => ({ ...prev, [key]: true }));
    try {
      let payload = {
        locationId: location.id,
        applicationFeeAmount: location.appFee,
        applicationFeeUnit: location.appFeeUnit || 'percentage'
      };
      let data = await updateLocationAppFee(payload);
      if (data.status === 200 || data.status === 201) {
        toast.success('Location fee updated');
      }
    } catch (err) {
      toast.error('Failed to update location fee');
    } finally {
      setSaving((prev) => ({ ...prev, [key]: false }));
    }
  };

  const onPageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    try {
      let data = await getAllBusinesses();
      console.log(data, '=');
      if (data.status === 200 || data.status === 201) {
        setBusinesses(data?.data?.data);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const paginationWrapper = 'dataTables_paginate paging_simple_numbers';
  const pagination = 'pagination pagination-rounded';

  return (
    <div className="p-3">
      <h4 className="mb-4">Business & Location App Fees</h4>

      <div>
        <div className="mb-4">
          <Input
            type="text"
            placeholder="Search by Id, business or location name..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="d-flex flex-column gap-4">
          {paginatedBusinesses.length === 0 ? (
            <p className="text-muted">No results found.</p>
          ) : (
            paginatedBusinesses.map((business) => (
              <Card key={business.id}>
                <CardBody className="m-0 p-0">
                  <Table bordered responsive className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Id</th>
                        <th style={{ width: '20%' }}>Name</th>
                        <th style={{ width: '20%' }}>Email</th>
                        <th style={{ width: '20%' }}>Phone</th>
                        <th style={{ width: '25%' }}>App Fee</th>
                        <th style={{ width: '15%' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="align-middle">{business?.id || ''}</td>
                        <td className="align-middle">
                          <strong>{business.name}</strong>
                        </td>
                        <td className="align-middle">{business.email}</td>
                        <td className="align-middle">{business.phoneNumber}</td>
                        <td className="align-middle">
                          <div className="d-flex align-items-center gap-2 align-middle">
                            <Input
                              type="number"
                              min="0"
                              value={business.defaultAppFee}
                              onChange={(e) => handleBusinessFeeChange(business.id, e.target.value)}
                              style={{ width: 80 }}
                            />
                            <Input
                              type="select"
                              value={business.defaultAppFeeUnit}
                              onChange={(e) =>
                                handleBusinessFeeUnitChange(business.id, e.target.value)
                              }
                              style={{ width: 100 }}
                            >
                              <option value="percentage">%</option>
                              <option value="amount">£</option>
                            </Input>
                          </div>
                        </td>
                        <td>
                          <Button
                            color="primary"
                            size="md"
                            onClick={() => saveBusinessFee(business)}
                            disabled={saving[`business-${business.id}`]}
                          >
                            {saving[`business-${business.id}`] ? (
                              <>
                                <Spinner size="sm" /> Saving...
                              </>
                            ) : (
                              'Save Business Fee'
                            )}
                          </Button>
                        </td>
                      </tr>
                      {business?.locations.map((location) => (
                        <tr key={location.id}>
                          <td className="align-middle">{location?.id || ''}</td>
                          <td className="align-middle">{location.name}</td>
                          <td className="align-middle">{location.email || '—'}</td>
                          <td className="align-middle">{location.phoneNumber || '—'}</td>
                          <td className="align-middle">
                            <div className="d-flex align-items-center gap-2">
                              <Input
                                type="number"
                                min="0"
                                value={location.appFee}
                                onChange={(e) =>
                                  handleLocationFeeChange(business.id, location.id, e.target.value)
                                }
                                style={{ width: 80 }}
                              />
                              <Input
                                type="select"
                                value={location.appFeeUnit}
                                onChange={(e) =>
                                  handleLocationFeeUnitChange(
                                    business.id,
                                    location.id,
                                    e.target.value
                                  )
                                }
                                style={{ width: 100 }}
                              >
                                <option value="percentage">%</option>
                                <option value="amount">£</option>
                              </Input>
                            </div>
                          </td>
                          <td>
                            <Button
                              color="primary"
                              size="md"
                              onClick={() => saveLocationFee(location)}
                              disabled={saving[`location-${location.id}`]}
                            >
                              {saving[`location-${location.id}`] ? (
                                <>
                                  <Spinner size="sm" /> Saving...
                                </>
                              ) : (
                                'Save Location Fee'
                              )}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            ))
          )}
        </div>

        {/* Pagination */}
        <Row className="mt-4">
          <Col sm={12} className="d-flex justify-content-end">
            <div className={paginationWrapper}>
              <ul className={pagination}>
                <li
                  className={`paginate_button page-item previous ${currentPage === 1 ? 'disabled' : ''}`}
                >
                  <Link to="#" className="page-link" onClick={() => onPageChange(currentPage - 1)}>
                    <i className="mdi mdi-chevron-left"></i>
                  </Link>
                </li>

                {Array.from({ length: totalPages }, (_, i) => (
                  <li
                    key={i}
                    className={`paginate_button page-item ${currentPage === i + 1 ? 'active' : ''}`}
                  >
                    <Link to="#" className="page-link" onClick={() => onPageChange(i + 1)}>
                      {i + 1}
                    </Link>
                  </li>
                ))}

                <li
                  className={`paginate_button page-item next ${currentPage === totalPages ? 'disabled' : ''}`}
                >
                  <Link to="#" className="page-link" onClick={() => onPageChange(currentPage + 1)}>
                    <i className="mdi mdi-chevron-right"></i>
                  </Link>
                </li>
              </ul>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default BusinessLocationAppFees;
